// Teleredo Design System — Primary: Purple #7A3FF2 · Accent: Teal #00C8C8
// Soft surfaces, layered shadows, clean borders. No backdrop-filter on regular surfaces.
// Frosted glass (BLUR) reserved for overlays: dialogs, menus, tooltips, appbar.

export const BLUR    = "blur(16px) saturate(1.6)";
export const BLUR_SM = "blur(10px) saturate(1.4)";

export const PAGE_BG      = "#F7F8FC";
export const PAGE_BG_DARK = "#0B0E1A";

// ─── Light mode surface tokens ─────────────────────────────────────────────
export const N = {
  bg:       "#FFFFFF",
  bgStrong: "#F7F8FC",
  bgInset:  "#F0EAFB",
  border:   "#E2E8F0",
  borderSm: "#F1F5F9",
  specular: "rgba(122,63,242,0.06)",
  text:     "#101828",
  sub:      "#64748B",
  muted:    "#94A3B8",
  brand:    "#7A3FF2",
  brand2:   "#9A6BF7",
  teal:     "#00C8C8",
  green:    "#22C55E",
  red:      "#EF4444",
  amber:    "#F59E0B",
  blue:     "#3B82F6",
  blur:     BLUR,
  blurSm:   BLUR_SM,
};

// ─── Dark mode surface tokens ───────────────────────────────────────────────
export const ND = {
  bg:       "#111827",
  bgStrong: "#1E293B",
  bgInset:  "#1A1130",
  border:   "#334155",
  borderSm: "#1E293B",
  specular: "rgba(122,63,242,0.10)",
  text:     "#F8FAFC",
  sub:      "#94A3B8",
  muted:    "#475569",
  brand:    "#9A6BF7",
  brand2:   "#B794FF",
  teal:     "#00C8C8",
  green:    "#22C55E",
  red:      "#EF4444",
  amber:    "#F59E0B",
  blue:     "#60A5FA",
  blur:     BLUR,
  blurSm:   BLUR_SM,
};

// ─── Light clay shadows ─────────────────────────────────────────────────────
export const raise   = `0 4px 16px rgba(15,23,42,0.08), 0 1px 4px rgba(15,23,42,0.04)`;
export const raiseMd = `0 2px 8px rgba(15,23,42,0.07), 0 1px 3px rgba(15,23,42,0.04)`;
export const raiseSm = `0 1px 3px rgba(15,23,42,0.05), 0 1px 2px rgba(15,23,42,0.03)`;
export const inset   = `inset 0 2px 6px rgba(15,23,42,0.07)`;
export const insetSm = `inset 0 1px 4px rgba(15,23,42,0.05)`;

// ─── Dark clay shadows ──────────────────────────────────────────────────────
export const dRaise   = `0 4px 20px rgba(0,0,0,0.45), 0 1px 6px rgba(0,0,0,0.24)`;
export const dRaiseMd = `0 2px 10px rgba(0,0,0,0.35), 0 1px 4px rgba(0,0,0,0.20)`;
export const dRaiseSm = `0 1px 6px rgba(0,0,0,0.28), 0 1px 2px rgba(0,0,0,0.18)`;
export const dInset   = `inset 0 2px 6px rgba(0,0,0,0.22)`;
export const dInsetSm = `inset 0 1px 4px rgba(0,0,0,0.18)`;

// ─── Solid fallbacks (opaque — same as main values) ─────────────────────────
export const NFallback = {
  bg:       "#FFFFFF",
  bgStrong: "#F7F8FC",
  bgInset:  "#EEF2FF",
};
export const NDFallback = {
  bg:       "#111827",
  bgStrong: "#1E293B",
  bgInset:  "#162032",
};

// ─── Helpers ────────────────────────────────────────────────────────────────
export const neuTokens  = (isLight = true) => (isLight ? N : ND);

export const neuShadows = (isLight = true) => {
  const T = isLight ? N : ND;
  return isLight
    ? { raise, raiseMd, raiseSm, inset, insetSm, blur: BLUR, blurSm: BLUR_SM, border: T.border, borderSm: T.borderSm }
    : { raise: dRaise, raiseMd: dRaiseMd, raiseSm: dRaiseSm, inset: dInset, insetSm: dInsetSm, blur: BLUR, blurSm: BLUR_SM, border: T.border, borderSm: T.borderSm };
};

export const neuCardSx = (isLight = true) => {
  const T = isLight ? N : ND;
  return {
    background:   T.bg,
    border:       `1px solid ${T.border}`,
    boxShadow:    isLight ? raiseMd : dRaiseMd,
    borderRadius: "18px",
  };
};

export const neuSmCardSx = (isLight = true) => {
  const T = isLight ? N : ND;
  return {
    background:   T.bg,
    border:       `1px solid ${T.border}`,
    boxShadow:    isLight ? raiseSm : dRaiseSm,
    borderRadius: "12px",
  };
};

export const neuInsetSx = (isLight = true) => {
  const T = isLight ? N : ND;
  return {
    background:   T.bgInset,
    border:       `1px solid ${T.borderSm}`,
    boxShadow:    isLight ? insetSm : dInsetSm,
    borderRadius: "10px",
  };
};

export const neuStatCardSx = (isLight = true) => {
  const T = isLight ? N : ND;
  return {
    background:   T.bg,
    border:       `1px solid ${T.border}`,
    boxShadow:    isLight ? raise : dRaise,
    borderRadius: "20px",
    padding:      "24px",
    transition:   "box-shadow 0.22s ease, transform 0.22s ease",
  };
};

export const neuSectionSx = (isLight = true) => {
  const T = isLight ? N : ND;
  return {
    background:   T.bgStrong,
    border:       `1px solid ${T.border}`,
    boxShadow:    isLight ? raiseMd : dRaiseMd,
    borderRadius: "24px",
  };
};

export const neuChipSx = (isLight = true) => {
  const T = isLight ? N : ND;
  return {
    background:   T.bgInset,
    border:       `1px solid ${T.border}`,
    boxShadow:    "none",
    borderRadius: "8px",
    fontWeight:   500,
    fontSize:     "0.8125rem",
  };
};

export const neuTableContainerSx = (isLight = true) => {
  const T = isLight ? N : ND;
  return {
    background:   T.bg,
    border:       `1px solid ${T.border}`,
    boxShadow:    isLight ? raiseMd : dRaiseMd,
    borderRadius: "18px",
    overflow:     "hidden",
  };
};
